import { Injectable } from '@angular/core';
import { SkyResourceApi } from '../api/resource';
import {
  ISkyResourceAudio,
  ISkyResourceImage,
  ISkyResourceVideo,
  SkyResource,
  SkyResourceWordCard,
} from '../interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { normalizeLinkDomainForOsmi } from '@skyeng/libs/shared/misc';

@Injectable({ providedIn: 'root' })
export class SkyResourceService {
  constructor(private skyResourceApi: SkyResourceApi) {}

  loadAudio(audioId: number): Observable<ISkyResourceAudio> {
    return this.skyResourceApi.loadAudio(audioId).pipe(map(resource => this.normalizeResourceLink(resource)));
  }

  loadImage(imageId: number | string, isRetina?: boolean): Observable<ISkyResourceImage> {
    return this.skyResourceApi.loadImage(imageId, isRetina).pipe(
      map(resource => {
        return this.normalizeResourceLink(resource);
      }),
    );
  }

  loadSvgAsString(imageId: number): Observable<string> {
    return this.skyResourceApi.loadSvgAsString(imageId);
  }

  loadVideo(videoId: number): Observable<ISkyResourceVideo | undefined> {
    return this.skyResourceApi
      .loadVideo(videoId)
      .pipe(map(resource => (resource ? this.normalizeResourceLink(resource) : resource)));
  }

  loadWordCards(wordCardIds: number[]): Observable<SkyResourceWordCard[]> {
    return this.skyResourceApi
      .loadWordCards(wordCardIds)
      .pipe(map(resources => resources.map(resource => this.normalizeResourceLink(resource))));
  }

  uploadFileToS3(url: string, blob: Blob, blobData: {}, uploadEventHandlers: {} = {}): Observable<void> {
    const formData = new FormData();
    Object.keys(blobData).forEach(key => formData.append(key, blobData[key]));
    formData.append('success_action_status', '201');
    formData.append('file', blob);

    return this.skyResourceApi.uploadFileToS3(url, formData, uploadEventHandlers);
  }

  private normalizeResourceLink<T extends SkyResource>(resource: T): T {
    if (this.resourceWithUrl(resource)) {
      return {
        ...resource,
        url: normalizeLinkDomainForOsmi(resource.url, { useProdDomainForcibly: true }),
      };
    }

    if (this.resourceWithLink(resource)) {
      return {
        ...resource,
        link:
          resource.link !== null ? normalizeLinkDomainForOsmi(resource.link, { useProdDomainForcibly: true }) : null,
      };
    }

    if (this.resourceWithImg(resource)) {
      return {
        ...resource,
        img: normalizeLinkDomainForOsmi(resource.img, { useProdDomainForcibly: true }),
        img2x: normalizeLinkDomainForOsmi(resource.img2x, { useProdDomainForcibly: true }),
      };
    }

    return resource;
  }

  private resourceWithUrl(resource: SkyResource): resource is ISkyResourceVideo {
    return resource.hasOwnProperty('url');
  }

  private resourceWithLink(resource: SkyResource): resource is ISkyResourceAudio | ISkyResourceImage {
    return resource.hasOwnProperty('link');
  }

  private resourceWithImg(resource: SkyResource): resource is SkyResourceWordCard {
    return resource.hasOwnProperty('img') && resource.hasOwnProperty('img2x');
  }
}
